import { BodyLarge, Button, colorVariants, Description, Heading2 } from '@casavo/base-ui'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import React from 'react'

import BannerListComponent, { BannerListItem } from '@/components/BannerCasavoProperties/BannerList'
import ButtonCircleIcon from '@/components/ButtonCircleIcon'
import Icon from '@/components/Icon'
import { ThisIsACasavoClaim } from '@/components/InventoryBannerBig/ThisIsACasavoClaim'
import { ModalContextType } from '@/context/ModalContext'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { paddingBottom } from '@/shared-styles/styles.css'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { handleCasavoPropertyModalClick } from '@/utils/tracking/eventsHandlers'
import { dataLayerPush } from '@/utils/tracking/trackingUtils'

import { bannerListRight, casavoPropertiesModalContent } from '../style.css'

import { casavoPropertiesModalHeader, casavoPropertiesModalInner } from './styles.css'

const CasavoProperties: React.FC<{
  closeModal: ModalContextType['closeModal']
}> = ({ closeModal }) => {
  const { lang, t } = useTranslation()

  const sections = t<Array<{ items: BannerListItem[]; title: string }>>(
    'casavo-properties-banner:modal_list_items',
    {},
    { returnObjects: true }
  )

  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  const Illustration = () => (
    <img
      alt=""
      className={sprinkles({ marginX: 'auto' })}
      height={197}
      loading="lazy"
      src={`${imageBasePath}/images/concepts/we-buy-or-sell.svg`}
      width={197}
    />
  )

  return (
    <div className={casavoPropertiesModalContent}>
      <div className={casavoPropertiesModalInner}>
        <ButtonCircleIcon
          ariaLabel="close dialog"
          onClick={() => {
            closeModal()
            handleCasavoPropertyModalClick('Close')
          }}
        >
          <Icon.Close className={colorVariants.greyscale600} />
        </ButtonCircleIcon>
      </div>
      <div className={sprinkles({ alignItems: 'center', display: 'flex' })}>
        <div className={casavoPropertiesModalHeader}>
          <Heading2 className={sprinkles({ paddingY: 'l' })}>
            <Trans components={[<br key="0" />]} i18nKey="casavo-properties-banner:title" />
          </Heading2>
          <Description className={sprinkles({ marginBottom: 'xl' })}>
            <Trans
              components={[<p key="0" className={paddingBottom.m} />, <p key="1" />]}
              i18nKey="casavo-properties-banner:modal_subtitle"
            />
          </Description>
        </div>

        <Illustration />
      </div>
      <ThisIsACasavoClaim i18nKey="PropertyAdvantages:PropertyAdvantages_CasavoProperty_BannerIntro" />
      <div
        className={sprinkles({
          display: { tablet: 'flex' },
          paddingBottom: { mobile: 'm', tablet: 'xl' },
        })}
      >
        {sections[0] && (
          <div className={sprinkles({ flex: { tablet: 1 } })}>
            <BodyLarge strong className={sprinkles({ paddingY: 'l' })}>
              {sections[0].title}
            </BodyLarge>
            {sections[0].items.map((item, index) => (
              <BannerListComponent {...item} key={`modal-item-0-${index}`} />
            ))}
          </div>
        )}

        {isLocaleEnabledFor('casavoAncillaryServicesOffered', lang) && sections[1] && (
          <div className={bannerListRight}>
            <BodyLarge strong className={sprinkles({ paddingY: 'l' })}>
              {sections[1].title}
            </BodyLarge>
            {sections[1].items.map((item, index) => (
              <BannerListComponent {...item} key={`modal-item-1-${index}`} imageSize="m" />
            ))}
          </div>
        )}
      </div>
      <Button
        center
        className={sprinkles({ marginTop: 'l' })}
        label={t(`casavo-properties-banner:good_to_know`)}
        styleVariant="secondary"
        onClick={() => {
          closeModal()
          dataLayerPush({
            event: 'casavo.user.interactions',
            eventAction: 'Click',
            eventCategory: 'CasavoPropertyModal',
            eventLabel: 'Close',
          })
        }}
      />
    </div>
  )
}
export default CasavoProperties
